import type { UserRoleString } from "@nlc/shared-types";

import { ProjectGroupEnum, USER_ROLES } from "@nlc/shared-types";
import { DownloadIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { ProjectsTable } from "@/features/projects/list/ProjectsTable";
import { useGetPendingApplications } from "@/features/projects/pendingApplications/hooks/useGetPendingApplications";
import { PendingApplicationsTable } from "@/features/projects/pendingApplications/PendingApplicationsTable";
import { FeaturePageLayout } from "@/features/shared";
import { usePageTitle } from "@/hooks/usePageTitle";
import { usePaginationWithDynamicPageSize } from "@/hooks/usePaginationWithDynamicPageSize";

import { useExportProjectsCsv } from "./hooks/useExportProjectsCsv.tsx";
import { useGetProjectsList } from "./hooks/useGetProjectsList.tsx";
import { useGetAvailableTabs } from "./utils/availableTabs";

export function ListProjectsPage() {
  const [selectedTab, setSelectedTab] = useState("active");

  const { canExport, exportCsv, isExporting } = useExportProjectsCsv();

  const isPendingApplicationsTab =
    selectedTab === ProjectGroupEnum.PENDING_APPLICATIONS;

  const {
    debouncedQuery,
    inputValue,
    itemsPerPage,
    page,
    setInputValue,
    setItemsPerPage,
    setPage,
    setSortDescriptor,
    sortDescriptor,
    updateMetadata,
  } = usePaginationWithDynamicPageSize();

  const { data: response, isFetching: isFetchingProjects } = useGetProjectsList(
    {
      direction: sortDescriptor.direction,
      group: selectedTab,
      itemsPerPage,
      page,
      search: debouncedQuery,
      sortBy: sortDescriptor?.column as string,
    },
  );

  const { data: pendingApplicationsData, isFetching: isFetchingApplications } =
    useGetPendingApplications(isPendingApplicationsTab);

  const tabs = useGetAvailableTabs();

  const projects = response?.projects || [];
  const metadata = response?.metadata;
  const pendingApplications = pendingApplicationsData?.applications || [];

  const isFetching = isPendingApplicationsTab
    ? isFetchingApplications
    : isFetchingProjects;

  useEffect(() => {
    updateMetadata(metadata);
  }, [metadata, updateMetadata]);

  usePageTitle(
    `Accounts - ${selectedTab.charAt(0).toUpperCase() + selectedTab.slice(1)}`,
  );

  const actionButtons =
    canExport && !isPendingApplicationsTab
      ? [
          {
            icon: <DownloadIcon size={16} />,
            label: "Export CSV",
            loading: isExporting,
            onPress: () => exportCsv(),
            requiredRoles: [USER_ROLES.SUPER_ADMIN as UserRoleString],
            variant: "primary" as const,
          },
        ]
      : [];

  return (
    <div className="p-xl">
      <FeaturePageLayout
        actionButtons={actionButtons}
        isLoading={isFetching}
        searchBar={
          isPendingApplicationsTab
            ? undefined
            : {
                inputValue,
                setInputValue,
              }
        }
        tabs={{
          onSelectionChange: (key) => {
            setSelectedTab(key as string);
            setSortDescriptor({
              column: "createdAt",
              direction: "descending",
            });
            setPage(1);
          },
          selectedKey: selectedTab,
          tabs: tabs,
        }}
        title="Accounts"
      >
        {isPendingApplicationsTab ? (
          <PendingApplicationsTable
            applications={pendingApplications}
            isLoading={isFetching}
          />
        ) : (
          <ProjectsTable
            currentGroup={selectedTab}
            isLoading={isFetching}
            pagination={
              metadata
                ? {
                    metadata: {
                      currentPage: metadata.currentPage,
                      itemsPerPage: metadata.itemsPerPage,
                      totalItems: metadata.totalItems,
                      totalPages: metadata.totalPages,
                    },
                    onItemsPerPageChange: (newItemsPerPage) => {
                      setItemsPerPage(newItemsPerPage);
                      setPage(1);
                    },
                    onPageChange: setPage,
                    pageSizeOptions: [10, 25, 50, 100],
                    showPageSizeSelector: true,
                  }
                : undefined
            }
            projects={projects}
            setSortDescriptor={setSortDescriptor}
            sortDescriptor={sortDescriptor}
          />
        )}
      </FeaturePageLayout>
    </div>
  );
}
